import { useState } from 'react';
import {
    ArrowPathIcon,
    CheckCircleIcon,
    CheckIcon,
    ExclamationTriangleIcon,
    MicrophoneIcon,
    StopIcon,
} from '@heroicons/react/24/outline';

import type { HousekeepingQuickReport } from '../../../hooks/useHousekeepingQuickReport';
import BottomSheet from '../../components/BottomSheet';
import Button from '../../components/Button';
import Text from '../../components/Text';
import { t } from '../../i18n/langue';

import ChampTexte from './ChampTexte';

/**
 * Signalement « photo d'abord » — même logique que le web
 * (`useHousekeepingQuickReport`) : la photo en grand, puis ✓ envoyer, « urgent »
 * (panne seulement) ou reprendre. Le détail écrit reste replié.
 */
interface FeuilleSignalementProps {
    signalement: HousekeepingQuickReport;
}

export default function FeuilleSignalement({ signalement }: FeuilleSignalementProps) {
    const [detailOuvert, setDetailOuvert] = useState(false);
    const [detail, setDetail] = useState('');
    const panne = signalement.kind === 'issue';
    const titre = panne ? t('menage.signalement.probleme') : t('menage.signalement.objetTrouve');
    const fermer = () => {
        if (!signalement.isSending) signalement.cancel();
    };

    return (
        <BottomSheet ouverte onClose={fermer} libelle={titre} libelleFermeture={t('commun.fermer')}>
            {signalement.step === 'done' ? (
                <div role="status" className="flex flex-col items-center gap-3 py-8">
                    <CheckCircleIcon className="h-24 w-24 text-main-color" aria-hidden="true" />
                    <Text variante="titre" ton="marque" as="p">
                        {t('menage.signalement.envoye')}
                    </Text>
                </div>
            ) : (
                <div className="flex flex-col gap-3">
                    <Text variante="titre" as="h2">
                        {titre}
                    </Text>
                    {signalement.previewUrl && (
                        <img
                            src={signalement.previewUrl}
                            alt={t('menage.signalement.photo')}
                            className="max-h-[45vh] w-full rounded-2xl object-contain"
                        />
                    )}
                    {signalement.recordingSupported && (
                        <>
                            <Button
                                libelle={
                                    signalement.isRecording
                                        ? t('menage.signalement.arreter', { n: signalement.recordingSeconds })
                                        : signalement.audioPreviewUrl
                                          ? t('menage.signalement.reenregistrer')
                                          : t('menage.signalement.parler')
                                }
                                icone={signalement.isRecording ? StopIcon : MicrophoneIcon}
                                variante={signalement.isRecording ? 'danger' : 'secondaire'}
                                pleineLargeur
                                className="py-4"
                                disabled={signalement.isSending}
                                onPress={signalement.toggleRecording}
                            />
                            {signalement.recordingError && (
                                <div role="alert">
                                    <Text variante="petit" ton="danger">
                                        {signalement.recordingError === 'denied'
                                            ? t('menage.signalement.microRefuse')
                                            : t('menage.signalement.enregistrementImpossible')}
                                    </Text>
                                </div>
                            )}
                            {signalement.audioPreviewUrl && !signalement.isRecording && (
                                <audio controls src={signalement.audioPreviewUrl} className="w-full" />
                            )}
                        </>
                    )}
                    {signalement.isError && (
                        <div role="alert">
                            <Text variante="petit" ton="danger">
                                {t('menage.signalement.pasEnvoye')}
                            </Text>
                        </div>
                    )}
                    <Button
                        libelle={signalement.isSending ? t('menage.signalement.envoi') : t('menage.signalement.envoyer')}
                        icone={CheckIcon}
                        pleineLargeur
                        className="py-4"
                        disabled={signalement.isSending}
                        onPress={() => signalement.send({ detail })}
                    />
                    {panne && (
                        <Button
                            libelle={t('menage.signalement.urgent')}
                            icone={ExclamationTriangleIcon}
                            variante="danger"
                            pleineLargeur
                            className="py-4"
                            disabled={signalement.isSending}
                            onPress={() => signalement.send({ detail, urgent: true })}
                        />
                    )}
                    <Button
                        libelle={t('menage.signalement.reprendrePhoto')}
                        icone={ArrowPathIcon}
                        variante="secondaire"
                        pleineLargeur
                        disabled={signalement.isSending}
                        onPress={signalement.retake}
                    />
                    {detailOuvert ? (
                        <ChampTexte libelle={t('menage.signalement.detail')} valeur={detail} onChange={setDetail} />
                    ) : (
                        <Button
                            libelle={t('menage.signalement.ajouterDetail')}
                            variante="lien"
                            ariaExpanded={false}
                            onPress={() => setDetailOuvert(true)}
                        />
                    )}
                </div>
            )}
        </BottomSheet>
    );
}
